import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FoundryCatalogModel } from './schema';

type UseFoundryModelsResult = {
  catalogModels: FoundryCatalogModel[];
  loading: boolean;
  error: string | undefined;
};

type FoundryModelsResponse = {
  models: FoundryCatalogModel[];
};

export function useFoundryModels(enabled: boolean): UseFoundryModelsResult {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [catalogModels, setCatalogModels] = useState<FoundryCatalogModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      setCatalogModels([]);
      setLoading(false);
      setError(undefined);
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;

    const fetchModels = async () => {
      setLoading(true);
      setError(undefined);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-plugin');
        const { token } = await identityApi.getCredentials();
        const url = new URL(`${baseUrl}/foundry/models`);

        const response = await fetch(url.toString(), {
          signal: abortController.signal,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch Foundry models: ${response.status} ${response.statusText}`,
          );
        }

        const data = (await response.json()) as FoundryModelsResponse;

        if (isActive) {
          setCatalogModels(data.models ?? []);
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (isActive) {
          setError('Unable to load Foundry models. Please try again.');
          setCatalogModels([]);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchModels();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [enabled, discoveryApi, identityApi]);

  return { catalogModels, loading, error };
}
